import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetTaskTemplateByIdQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
} from '../../store/api';
import { toast } from 'react-toastify';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import {
  buildTaskTemplateRelationParams,
  TASK_TEMPLATE_PRIORITY_OPTIONS,
} from './taskTemplateApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('title', values.title ?? '');
  fd.append('priority', values.priority ?? '');
  fd.append('description', values.description ?? '');
  return fd;
};

const priorityOptionLabel = (value) => {
  const label = String(value || '');
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
};

const CreateTaskTemplate = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const {
    data: templateData,
    isLoading: templateLoading,
    isError: templateError,
    error: templateErr,
  } = useGetTaskTemplateByIdQuery(
    { id: editId, params: buildTaskTemplateRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createTaskTemplate, { isLoading: creating }] = useCreateTaskTemplateMutation();
  const [updateTaskTemplate, { isLoading: updating }] = useUpdateTaskTemplateMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (templateLoading || !hydrated);

  useEffect(() => {
    if (templateError) {
      toast.error(templateErr?.data?.message || 'Failed to load task template');
      navigate('/task-templates');
    }
  }, [templateError, templateErr, navigate]);

  useEffect(() => {
    if (!isEdit || !templateData || hydrated) return;
    const record = templateData?.data ?? templateData;
    if (!record) return;
    setTitle(record.title ?? '');
    setPriority(record.priority ?? 'medium');
    setDescription(record.description ?? '');
    setHydrated(true);
  }, [isEdit, templateData, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }
    setErrors({});

    const values = {
      title: title.trim(),
      priority: priority || 'medium',
      description: description.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateTaskTemplate({ id: editId, data: fd }).unwrap();
        toast.success('Task template updated');
      } else {
        await createTaskTemplate({ data: fd }).unwrap();
        toast.success('Task template added');
      }
      navigate('/task-templates');
    } catch (err) {
      applyApiError(
        err,
        isEdit ? 'Failed to update task template' : 'Failed to add task template'
      );
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-templates')} sx={{ mb: 2 }}>
          Back to task templates
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task template' : 'Add task template'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearError('title');
                  }}
                  required
                  fullWidth
                  {...fieldProps('title')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={TASK_TEMPLATE_PRIORITY_OPTIONS}
                  value={priority}
                  onChange={(_, value) => {
                    setPriority(value ?? 'medium');
                    clearError('priority');
                  }}
                  getOptionLabel={priorityOptionLabel}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Priority"
                      fullWidth
                      {...fieldProps('priority')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError('description');
                  }}
                  fullWidth
                  multiline
                  minRows={4}
                  {...fieldProps('description')}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Status will stay on the backend default.
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Add task template'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/task-templates')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskTemplate;
